import { useEffect, useState } from "react";
import axiosInstance from "../helpers/axiosInstance";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";

const VendorDetails = () => {
    const { id } = useParams();
    const [vendor, setVendor] = useState(null);
    const [entryPolicies, setEntryPolicies] = useState([]);
    const [tripData, setTripData] = useState([]);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchVendorDetailsAndTrips = async () => {
            try {
                // First API call to get vendor details
                const vendorRes = await axiosInstance.get(`/api/vendor/vendors/${id}`);
                const vendorDetails = vendorRes.data?.vendor;

                if (!vendorDetails) throw new Error("Vendor details not found.");

                setVendor(vendorDetails);
                setEntryPolicies(vendorRes.data?.entryPolicies || []);

                // Extract vendor_code
                const vendorCode = vendorDetails.vendor_code;

                if (!vendorCode) throw new Error("Vendor code is missing.");

                // Second API call using vendor_code
                const tripRes = await axiosInstance.get(`/api/trip/vendor-based-tripId/${vendorCode}`);
                const trips = tripRes.data?.data || [];

                const formattedTrips = trips.map(item => ({
                    route_name: item.route_name,
                    truck_length: item.truck_length,
                    is_deleted: item.is_deleted,
                }));

                setTripData(formattedTrips);

            } catch (err) {
                setError("Failed to fetch vendor or trip data.");
                console.error("Error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchVendorDetailsAndTrips();
    }, [id]);

    if (loading) return <p>Loading vendor details...</p>;
    if (error) return <p className="text-danger">{error}</p>;

    return (
        <div className="vendor-details-container mt-5 ms-5">
            <div className="row gap-5">
                <div className="col-md-5 mt-3 bg-white" style={{ height: "300px" }}>
                    {vendor && (
                        <div className="vendor-card p-4 rounded-lg shadow-md">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="info-item">
                                        <span className="info-label">Vendor Name</span>
                                        <h1 className="device-name">
                                            <i className="bi bi-person-circle"></i> {vendor.vendor_name}
                                        </h1>
                                    </div>
                                    <div className="info-item mt-4">
                                        <span className="info-label">Vendor Code</span>
                                        <div className="device-type">
                                            <i className="bi bi-upc"></i> {vendor.vendor_code}
                                        </div>
                                    </div>
                                </div>
                                <span className={`status-badge mb-5 ${vendor.is_active ? "active" : "inactive"}`}>
                                    {vendor.is_active ? "ACTIVE" : "INACTIVE"}
                                </span>
                            </div>

                            <button
                                className="view-history-button bg-blue-500 text-blue p-2 rounded mt-4"
                                onClick={() => setShowHistoryModal(true)}
                            >
                                <i className="bi bi-clock-history text-blue"></i> View Time Exclusion History
                            </button>
                        </div>
                    )}
                </div>

                <div
                    className="col-md-6 mt-3 bg-white p-4"
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                >
                    <h5 className="mb-3">Trip Information</h5>

                    {tripData.length === 0 ? (
                        <p className="text-muted">No trip information available.</p>
                    ) : (
                        <ul className="list-group">
                            {tripData.map((trip, index) => (
                                <li
                                    key={index}
                                    className="list-group-item border-0 p-0 mb-3"
                                    style={{ background: "transparent" }}
                                >
                                    <div className="bg-white rounded shadow-sm p-3 d-flex flex-row justify-content-between align-items-center">
                                        <div className="flex-fill px-2">
                                            <div className="text-muted small">Trip</div>
                                            <div className="fw-semibold">{trip.route_name}</div>
                                        </div>
                                        <div className="flex-fill px-2 text-center">
                                            <div className="text-muted small">Truck Length</div>
                                            <div className="fw-semibold">{trip.truck_length}</div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>

                    )}
                </div>
            </div>

            <div className="actions-section mt-3 border-0">
                <button className="back-button" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left"></i>
                    Back to Vendors
                </button>
            </div>

            {showHistoryModal && (
                <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="header-title">
                                <i className="bi bi-clock-history"></i>
                                <h3>Vendor Time Exclusion History</h3>
                            </div>
                            <button className="close-button" onClick={() => setShowHistoryModal(false)}>
                                <i className="bi bi-x-circle-fill"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="timeline">
                                {entryPolicies.length > 0 ? (
                                    [...entryPolicies]
                                        .sort((a, b) => b.id - a.id)
                                        .map((policy, index) => {
                                            const isLatest = index === 0;
                                            return (
                                                <div
                                                    key={index}
                                                    className={`timeline-item ${isLatest ? "latest-entry" : ""}`}
                                                    style={{
                                                        backgroundColor: isLatest ? "#D1E7DD" : "#fff",
                                                        borderLeft: isLatest ? "5px solid #15803d" : "none",
                                                        padding: "12px",
                                                        borderRadius: "6px",
                                                        marginBottom: "10px",
                                                        boxShadow: isLatest ? "0px 4px 6px rgba(0, 0, 0, 0.1)" : "none"
                                                    }}
                                                >
                                                    <div className="timeline-marker"></div>
                                                    <div className="timeline-content">
                                                        <div className="timeline-header">
                                                            <h6
                                                                className="status-text"
                                                                style={{ color: isLatest ? "#15803d" : "#555" }}
                                                            >
                                                                ADDED
                                                            </h6>
                                                        </div>
                                                        <p className="timestamp">
                                                            <i className="bi bi-calendar-event"></i> Updated At:{" "}
                                                            {new Date(policy.updatedAt).toLocaleString()}
                                                        </p>
                                                        <p className="location-text">
                                                            <i className="bi bi-clock"></i> Late Arrival: {policy.late_arrival_hour} hr | Early Entry: {policy.early_entry_hour} hr
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })
                                ) : (
                                    <p className="text-gray-500">No exclusion history available.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorDetails;
